// Read-only mirror of the editor's CoverView, used by the customer flow's
// preview step (and anywhere else we need to *show* a cover spread without
// any of the interactive editor controls). Markup matches AlbumEditorPage's
// CoverView so what the customer just designed is what they see here.

import { useEffect, useState } from 'react';
import { CoverElementsView } from '@/components/CoverElementsView';
import { faceAspect } from '@/lib/coverElements';
import {
  COVER_PANEL_ASPECT,
  CoverThumbnail,
  type TextZoneOverride,
  type ExtraTextZone,
  type CoverDesignTransform,
} from '@/components/CoverThumbnail';
import { cn } from '@/lib/utils';
import { sampleImageBackgroundHex } from '@/lib/coverColorSample';
import type { Cover } from '@/lib/api';

// Pinned to canonical brand tokens (lib/brand.ts).
const COLORS = {
  gold:       '#C9A24A',
  goldDark:   '#8B6F26',
  beigeLight: '#FAF6EC',
  muted:      '#6B7280',
};

export interface SpineConfigLike {
  text?: string;
  bgColor?: string;
  textColor?: string;
  fontSize?: number;
  rotation?: number;
  scale?: number;
}

export interface BackCoverImage {
  storage_path?: string;
  signed_url?: string | null;
  /** FULL-PAGE back photo (2026-07-15): renders full-bleed behind the grid. */
  full_page?: boolean;
}

export interface CoverSpreadPreviewProps {
  selectedCover: Cover | null;
  destination: string;
  year: string;
  coverDestText?: string;
  coverYearText?: string;
  coverZoneOverrides?: Record<number, TextZoneOverride>;
  coverExtraZones?: ExtraTextZone[];
  spineConfig?: SpineConfigLike;
  logoUrl?: string | null;
  backCoverImages?: BackCoverImage[];
  backCoverBgColor?: string | null;
  /** Front-cover background — user-set override (defaults to dominant_color). */
  frontCoverBgColor?: string | null;
  /** When true, hide the cover artwork so only the bg color shows. */
  hideFrontCoverImage?: boolean;
  /** Translate + scale applied to the cover design overlay. */
  coverDesignTransform?: CoverDesignTransform;
  /** Client-supplied front-cover image (signed URL) on a blank cover — drawn
   *  cover-fit behind the text zones, matching the editor + PDF. */
  blankCoverImageSrc?: string | null;
  /** Display-only pan/zoom of the blank-cover photo (parity w/ editor+PDF). */
  blankCoverTransform?: { x: number; y: number; scale: number };
  /** Constrain spread height. Default: viewport-aware like editor. */
  heightStyle?: string;
  /** COVER ELEMENTS (2026-10-20) — `cover_customizations.coverElements`:
   *  stickers / icons / photos the team placed on the faces. */
  coverElements?: unknown;
  /** Display copy of an album photo (photo elements). */
  photoUrl?: (photoId: string) => string | null;
  /** Print settings for the spine's real width (spine elements). */
  spineSettings?: { spineWmm: number; jointGapMm: number };
}

export function CoverSpreadPreview({
  selectedCover,
  destination,
  year,
  coverDestText,
  coverYearText,
  coverZoneOverrides = {},
  coverExtraZones = [],
  spineConfig = {},
  logoUrl = null,
  backCoverImages = [],
  backCoverBgColor = null,
  frontCoverBgColor = null,
  hideFrontCoverImage = false,
  coverDesignTransform,
  blankCoverImageSrc = null,
  blankCoverTransform,
  // The viewport-width term used to deduct only 1.5rem (= self px-3 *2),
  // which assumes zero parent padding. In practice the customer flow wraps
  // this in a `px-4 py-6` container, so the real budget is parent 2rem +
  // self 1.5rem = 3.5rem. With the old number, iPhone-width screens cropped
  // the front-cover right edge (the "Bali" → "Bal" + cake half-hidden bug).
  heightStyle = 'min(75vh, 560px, calc((100vw - 3.5rem) / 1.55))',
  coverElements,
  photoUrl,
  spineSettings,
}: CoverSpreadPreviewProps) {
  // Sample the live cover image's background plate (corners + edge
  // midpoints) so the back cover + spine match the FRONT cover exactly.
  // The DB's `cover.dominant_color` is sometimes a rough average that
  // visibly differs from the flat plate behind the artwork — the user's
  // BALI screenshot showed back/spine in a lighter blue than the front.
  // Sampling solves this without any DB rewrite.
  const [sampledBg, setSampledBg] = useState<string | null>(null);
  const coverImgUrl = selectedCover?.thumbnail_url ?? null;
  useEffect(() => {
    let cancelled = false;
    setSampledBg(null);
    if (!coverImgUrl) return;
    sampleImageBackgroundHex(coverImgUrl)
      .then((hex) => { if (!cancelled) setSampledBg(hex); })
      .catch(() => { /* fall back to dominant_color */ });
    return () => { cancelled = true; };
  }, [coverImgUrl]);

  // Priority must match pdfGenerator's so the editor preview is a faithful
  // mock of the exported PDF.
  //
  //   1. backCoverBgColor   — user explicit pick (overrides everything)
  //   2. dominant_color     — admin's curated value (now editable via the
  //                           cover admin's bg color picker). When the admin
  //                           changes a cover's bg, that pick MUST drive the
  //                           spine + back-cover render, even if a pixel
  //                           sample of the stale thumbnail returns a
  //                           different colour.
  //   3. sampledBg          — pixel sample of the front-cover seam, used
  //                           only as a legacy fallback for rows that
  //                           somehow have no dominant_color.
  //   4. cream              — last-resort fallback for blank covers
  const coverBgColor =
    backCoverBgColor
    ?? selectedCover?.dominant_color
    ?? sampledBg
    ?? COLORS.beigeLight;
  const isBlankTheme =
    selectedCover?.id === '__blank__' ||
    (selectedCover as unknown as { back_cover_style?: string })?.back_cover_style === 'blank';
  const imgs = backCoverImages.filter((i): i is BackCoverImage => !!i);
  const hasImages = imgs.length > 0;

  const spineBg = spineConfig.bgColor ?? coverBgColor;
  const spineText = (spineConfig.text ?? destination ?? '').trim() || 'DESTINATION';
  // Spine text colour follows the front-cover destination zone's colour
  // when spineConfig hasn't set an explicit override. The editor has a
  // useEffect that propagates the destination colour into spineConfig
  // when the customer opens the editor — but the magic-flow preview is
  // reached BEFORE the editor mounts, so spineConfig.textColor is still
  // null and the spine used to render white on a red-title cover. Mirror
  // the same resolution chain inline so the preview matches the editor.
  const zones = selectedCover?.text_zones ?? [];
  const destIdx = zones.findIndex((z) => z.type === 'destination');
  const destZoneColor =
    (destIdx >= 0 ? coverZoneOverrides[destIdx]?.color : null) ??
    (destIdx >= 0 ? zones[destIdx]?.color : null) ??
    null;
  const spineTextColor = spineConfig.textColor ?? destZoneColor ?? '#FFFFFF';
  // Mirror the PDF's spine-text sizing (generateCoverPdf) so the editor shows
  // what actually prints. The text lives in the 14 mm printed spine COLUMN but
  // the PDF caps its glyph height to the ~8 mm rigid hardcover board (≤80% of
  // it). We express that as a fraction of the spine column and render it in
  // `cqw` so it tracks the column width responsively — which also stops the
  // text from overflowing the (narrower) mobile spine column.
  const SPINE_COL_PT = 14 * 2.8346;               // printed spine column ≈ 39.68pt
  const SPINE_MAX_EM_PT = (8 * 2.8346 * 0.8) / 0.73; // font size whose glyph fits the rigid board ≈ 24.85pt
  const spineRequestedPt = (spineConfig.fontSize ?? 28) * (spineConfig.scale ?? 1);
  const spineEmCqw = (Math.min(spineRequestedPt, SPINE_MAX_EM_PT) / SPINE_COL_PT) * 100;
  const spineRotation = spineConfig.rotation ?? 0;

  return (
    <div
      className="flex items-center justify-center w-full h-full px-3 md:px-8"
      // Establish an inline-size container so the inner spread can derive
      // its height from the ACTUAL available width via `cqi` units. The
      // previous viewport-based formula (100vw - 3.5rem / 1.55) was wrong
      // whenever the spread sat inside a max-w-4xl + px-4 column or a
      // device-frame preview where 100vw didn't match the content width —
      // the right cover got clipped.
      style={{ containerType: 'inline-size' }}
    >
      <div
        className="flex items-stretch"
        style={{
          // Cap by the original viewport/parent constraint, but ALSO clamp
          // to whatever fits horizontally in the current container. The 56
          // (spine_px worst-case = w-14 on desktop) + 1.333 (= 2 × 200/300,
          // the print's image frame) give us the largest height where the
          // back + spine + front fit side-by-side. On mobile the spine is 28px
          // so this leaves a 28-pixel slack — visually fine, never crops.
          height: `min(${heightStyle}, calc((100cqi - 56px) / 1.3334))`,
        }}
      >
        {/* Back Cover — the print's 200 × 300 mm frame (COVER_PANEL_ASPECT),
            as the editor shows it. */}
        <div
          className="rounded-l-md overflow-hidden relative flex flex-col items-center justify-end py-4 px-4 shrink-0"
          style={{
            aspectRatio: COVER_PANEL_ASPECT,
            height: '100%',
            background: coverBgColor,
            boxShadow: '-4px 2px 20px rgba(0,0,0,0.1)',
          }}
        >
          {isBlankTheme && hasImages ? (
            <div className="absolute inset-0">
              {imgs[0]?.signed_url ? (
                <img
                  src={imgs[0].signed_url}
                  alt="Quatrième de couverture"
                  className="w-full h-full object-cover"
                  draggable={false}
                />
              ) : (
                <div className="w-full h-full" />
              )}
            </div>
          ) : !isBlankTheme && hasImages ? (() => {
            /* FULL-PAGE (2026-07-15): a full_page-flagged image renders
               full-bleed behind; the others keep the centred grid — mirrors
               the editor + PDF (parity). */
            const fullImg = imgs.find((im) => im.full_page) ?? null;
            const gridImgs = imgs.filter((im) => !im.full_page);
            return (
            <>
            {fullImg && (
              <div className="absolute inset-0">
                {fullImg.signed_url ? (
                  <img src={fullImg.signed_url} alt="Photo pleine page" className="w-full h-full object-cover" draggable={false} loading="lazy" />
                ) : (
                  <div className="w-full h-full" />
                )}
              </div>
            )}
            {gridImgs.length > 0 && (
            <div className="flex-1 flex items-center justify-center w-full px-4 pt-4 relative z-[1]">
              <div className="relative w-[70%] aspect-square">
                <div
                  className={cn(
                    'w-full h-full grid gap-1.5 rounded-lg overflow-hidden',
                    gridImgs.length === 1 ? 'grid-cols-1 grid-rows-1' :
                    gridImgs.length === 2 ? 'grid-cols-2 grid-rows-1' :
                    'grid-cols-2 grid-rows-2'
                  )}
                >
                  {gridImgs.map((img, i) => (
                    <div
                      key={img.storage_path ?? i}
                      className={cn(
                        'relative overflow-hidden rounded-md',
                        gridImgs.length === 3 && i === 2 ? 'col-span-2' : ''
                      )}
                    >
                      {img.signed_url ? (
                        <img
                          src={img.signed_url}
                          alt={`Photo ${i + 1}`}
                          className="w-full h-full object-cover"
                          draggable={false}
                          loading="lazy"
                        />
                      ) : (
                        <div className="w-full h-full bg-white/10" />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
            )}
            </>
            );
          })() : (
            // No back-cover photos: empty space (matches editor's empty state
            // minus the upload button; we don't want any clickable surface
            // on the preview).
            <div className="flex-1" />
          )}

          {/* Logo at bottom */}
          <div
            className="relative z-10 shrink-0 flex items-center justify-center mb-2"
            style={{ height: 28 }}
          >
            {logoUrl ? (
              <img
                src={logoUrl}
                alt="Storiz"
                className="max-h-full max-w-full object-contain"
                style={{ opacity: hasImages ? 0.8 : 0.6 }}
                draggable={false}
              />
            ) : (
              <span
                className="text-[8px] tracking-widest uppercase"
                style={{ color: hasImages ? 'rgba(255,255,255,0.7)' : 'rgba(255,255,255,0.5)' }}
              >
                Storiz
              </span>
            )}
          </div>

          <div
            className="absolute bottom-1.5 left-0 right-0 text-center text-[7px] tracking-widest uppercase pointer-events-none"
            style={{ color: hasImages ? 'rgba(255,255,255,0.4)' : 'rgba(255,255,255,0.35)' }}
          >
            Quatrième de couverture
          </div>
          <CoverElementsView elements={coverElements} face="back" photoUrl={photoUrl} style={{ inset: 0 }} />
        </div>

        {/* Spine */}
        <div
          className="relative overflow-hidden w-7 md:w-14"
          style={{
            height: '100%',
            background: spineBg,
            boxShadow: 'inset 2px 0 4px rgba(0,0,0,0.15), inset -2px 0 4px rgba(0,0,0,0.15)',
            // Query container so the spine text below can size itself as a
            // fraction of THIS column's width (cqw), matching the PDF.
            containerType: 'inline-size',
          }}
        >
          <div
            className="absolute inset-0 flex items-center justify-center"
            style={{ writingMode: 'vertical-rl', textOrientation: 'mixed' }}
          >
            <span
              className="font-bold tracking-wider uppercase whitespace-nowrap"
              style={{
                color: spineTextColor,
                fontSize: `${spineEmCqw}cqw`,
                fontFamily: "'Londrina Solid', sans-serif",
                transform: `rotate(${spineRotation}deg)`,
                letterSpacing: '0.12em',
              }}
            >
              {spineText}
            </span>
          </div>
          <CoverElementsView
            elements={coverElements}
            face="spine"
            photoUrl={photoUrl}
            style={{ top: 0, bottom: 0, left: '50%', height: '100%', aspectRatio: String(faceAspect('spine', spineSettings)), transform: 'translateX(-50%)' }}
          />
        </div>

        {/* Front Cover */}
        <div
          className="rounded-r-md overflow-hidden relative shrink-0"
          style={{
            aspectRatio: COVER_PANEL_ASPECT,
            height: '100%',
            boxShadow: '4px 2px 20px rgba(0,0,0,0.12)',
            border: `1px solid ${COLORS.gold}15`,
            // Editor's CoverView paints `frontCoverBgColor ?? dominant_color`.
            // We fall further through the back-cover chain so when only one
            // panel has an explicit override the three still read as one plate.
            background: frontCoverBgColor ?? coverBgColor,
          }}
        >
          {selectedCover ? (
            <CoverThumbnail
              cover={selectedCover}
              className="w-full h-full"
              destinationText={coverDestText || destination}
              yearText={coverYearText || year}
              zoneOverrides={coverZoneOverrides}
              extraZones={coverExtraZones}
              bgColorOverride={frontCoverBgColor}
              hideThumbnail={hideFrontCoverImage}
              designTransform={coverDesignTransform}
              blankCoverImageSrc={blankCoverImageSrc}
              blankCoverTransform={blankCoverTransform}
            />
          ) : (
            <div className="w-full h-full flex flex-col items-center justify-center px-6 text-center">
              <div
                className="text-4xl font-bold uppercase tracking-tight mb-2"
                style={{
                  fontFamily: "'Fraunces', 'Cormorant Garamond', Georgia, serif",
                  color: COLORS.gold,
                  lineHeight: 1,
                }}
              >
                {coverDestText || destination || 'DESTINATION'}
              </div>
              <div
                className="text-2xl font-light"
                style={{
                  fontFamily: "'Fraunces', 'Cormorant Garamond', Georgia, serif",
                  color: COLORS.goldDark,
                }}
              >
                {coverYearText || year || new Date().getFullYear()}
              </div>
            </div>
          )}
          <div
            className="absolute bottom-2 left-0 right-0 text-center text-[8px] tracking-widest uppercase pointer-events-none"
            style={{
              color: selectedCover?.thumbnail_url ? 'rgba(255,255,255,0.5)' : `${COLORS.muted}50`,
            }}
          >
            Couverture avant
          </div>
          <CoverElementsView elements={coverElements} face="front" photoUrl={photoUrl} style={{ inset: 0 }} />
        </div>
      </div>
    </div>
  );
}
